import { ref, onUnmounted } from 'vue'
import type { User as OidcUser } from 'oidc-client-ts'
import { defaultOidcConfig } from '@/config/oidc'

// BroadcastChannel for cross-tab communication
const BROADCAST_CHANNEL_NAME = 'oidc-auth-channel'
const REFRESH_LOCK_NAME = 'oidc-token-refresh'
const REFRESH_FLAG_KEY = 'oidc.refresh-in-progress'
const REFRESH_FLAG_TIMEOUT = 10000 // 10 seconds
const REFRESH_WAIT_INTERVAL = 200 // Poll every 200ms
const REFRESH_WAIT_MAX = 30000 // Max 30 seconds wait

// Generate unique tab ID
const tabId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

// BroadcastChannel instance (singleton)
let broadcastChannel: BroadcastChannel | null = null

// Get localStorage key for user data
function getUserStorageKey(): string {
  const authority = defaultOidcConfig.authority
  const clientId = defaultOidcConfig.client_id
  return `oidc.user:${authority}:${clientId}`
}

// Parse user data from localStorage
function parseUserFromStorage(): OidcUser | null {
  try {
    const key = getUserStorageKey()
    const stored = localStorage.getItem(key)
    if (!stored) return null

    const userData = JSON.parse(stored)

    // Check if token is expired
    if (userData.expires_at) {
      const expiresAt = userData.expires_at * 1000 // Convert to milliseconds
      if (Date.now() >= expiresAt) {
        return null // Token expired
      }
    }

    return userData as OidcUser
  } catch (err) {
    console.error('Error parsing user from storage:', err)
    return null
  }
}

// Check if refresh is in progress (localStorage fallback)
function isRefreshInProgress(): boolean {
  try {
    const flag = localStorage.getItem(REFRESH_FLAG_KEY)
    if (!flag) return false

    const timestamp = parseInt(flag, 10)
    const now = Date.now()

    // Check if flag expired
    if (now - timestamp > REFRESH_FLAG_TIMEOUT) {
      localStorage.removeItem(REFRESH_FLAG_KEY)
      return false
    }

    return true
  } catch {
    return false
  }
}

// Set refresh in progress flag
function setRefreshInProgress(): void {
  try {
    localStorage.setItem(REFRESH_FLAG_KEY, Date.now().toString())
  } catch {
    // Ignore storage errors
  }
}

// Clear refresh in progress flag
function clearRefreshInProgress(): void {
  try {
    localStorage.removeItem(REFRESH_FLAG_KEY)
  } catch {
    // Ignore storage errors
  }
}

// Wait for refresh to complete (localStorage fallback)
async function waitForRefreshCompletion(): Promise<boolean> {
  const startTime = Date.now()
  const initialAccessToken = parseUserFromStorage()?.access_token

  while (Date.now() - startTime < REFRESH_WAIT_MAX) {
    if (!isRefreshInProgress()) {
      // Check if user data changed (access token updated)
      const currentUser = parseUserFromStorage()
      if (currentUser && currentUser.access_token !== initialAccessToken) {
        return true // Refresh completed
      }
      return false // Refresh flag cleared but no update
    }

    await new Promise(resolve => setTimeout(resolve, REFRESH_WAIT_INTERVAL))
  }

  return false // Timeout
}

// Get or create BroadcastChannel
function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') {
    return null
  }

  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
  }

  return broadcastChannel
}

// Broadcast message to other tabs
function broadcastMessage(type: string, data?: Record<string, unknown>): void {
  const channel = getBroadcastChannel()
  if (!channel) return

  try {
    channel.postMessage({
      type,
      tabId,
      timestamp: Date.now(),
      ...data,
    })
  } catch (err) {
    console.error('Error broadcasting message:', err)
  }
}

export interface FastAuthResult {
  user: OidcUser | null
  isValid: boolean
}

/**
 * Fast authentication check by reading directly from localStorage
 * Avoids calling getUser() which may process cookies or make network calls
 */
export function fastCheckAuth(): FastAuthResult {
  const user = parseUserFromStorage()
  return {
    user,
    isValid: user !== null && !user.expired,
  }
}

/**
 * Acquire refresh lock using Navigator.locks API (with localStorage fallback)
 * Returns true if lock acquired, false if another tab is refreshing
 */
export async function acquireRefreshLock(): Promise<boolean> {
  // Try Navigator.locks API first
  if (typeof navigator !== 'undefined' && navigator.locks) {
    try {
      return await new Promise<boolean>((resolve) => {
        navigator.locks.request(
          REFRESH_LOCK_NAME,
          { ifAvailable: true },
          (lock) => {
            if (lock) {
              // Lock acquired
              broadcastMessage('refresh-started')
              resolve(true)
            } else {
              // Lock not available, another tab is refreshing
              resolve(false)
            }
          }
        )
      })
    } catch (err) {
      console.warn('Navigator.locks not available, using localStorage fallback:', err)
      // Fall through to localStorage fallback
    }
  }

  // Fallback to localStorage flag
  if (isRefreshInProgress()) {
    return false // Another tab is refreshing
  }

  setRefreshInProgress()
  broadcastMessage('refresh-started')
  return true
}

/**
 * Release refresh lock
 */
export async function releaseRefreshLock(): Promise<void> {
  // Navigator.locks releases automatically when callback completes
  // For localStorage fallback, clear the flag
  clearRefreshInProgress()
  broadcastMessage('refresh-completed')
}

/**
 * Wait for another tab to complete refresh
 */
export async function waitForRefresh(): Promise<boolean> {
  // If using Navigator.locks, wait for lock to be released
  if (typeof navigator !== 'undefined' && navigator.locks) {
    try {
      return await new Promise<boolean>((resolve) => {
        navigator.locks.request(
          REFRESH_LOCK_NAME,
          { mode: 'shared' }, // Wait for exclusive lock to be released
          async () => {
            // Lock acquired (refresh completed)
            // Wait a bit for the other tab to update localStorage
            await new Promise(r => setTimeout(r, 100))
            resolve(true)
          }
        )
      })
    } catch (err) {
      console.warn('Error waiting for lock:', err)
      // Fall through to localStorage fallback
    }
  }

  // Fallback: poll localStorage
  return await waitForRefreshCompletion()
}

/**
 * Broadcast token update to other tabs
 */
export function broadcastTokenUpdate(): void {
  broadcastMessage('token-updated')
}

/**
 * Broadcast token expiring event
 */
export function broadcastTokenExpiring(): void {
  broadcastMessage('token-expiring')
}

/**
 * Broadcast token expired event
 */
export function broadcastTokenExpired(): void {
  broadcastMessage('token-expired')
}

/**
 * Broadcast user logged out event
 */
export function broadcastUserLoggedOut(): void {
  broadcastMessage('user-logged-out')
}

/**
 * Set up listener for cross-tab messages
 * Returns cleanup function
 */
export function listenForTokenUpdates(
  onTokenUpdated: () => void,
  onTokenExpired: () => void,
  onUserLoggedOut: () => void
): () => void {
  const channel = getBroadcastChannel()
  if (!channel) {
    // Fallback to storage events for older browsers
    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === getUserStorageKey() && e.newValue) {
        // User data updated in localStorage
        onTokenUpdated()
      }
    }

    window.addEventListener('storage', handleStorageEvent)
    return () => {
      window.removeEventListener('storage', handleStorageEvent)
    }
  }

  const handleMessage = (event: MessageEvent) => {
    // Ignore messages from this tab
    if (event.data.tabId === tabId) {
      return
    }

    switch (event.data.type) {
      case 'token-updated':
      case 'refresh-completed':
        onTokenUpdated()
        break
      case 'token-expired':
        onTokenExpired()
        break
      case 'user-logged-out':
        onUserLoggedOut()
        break
      case 'refresh-started':
        // Another tab started refresh, we should wait if we need to refresh
        break
    }
  }

  channel.addEventListener('message', handleMessage)
  return () => {
    channel.removeEventListener('message', handleMessage)
  }
}

/**
 * Composable for cross-tab authentication coordination
 */
export function useCrossTabAuth() {
  const isInitialized = ref(false)

  const initialize = (
    onTokenUpdated: () => void,
    onTokenExpired: () => void,
    onUserLoggedOut: () => void
  ) => {
    if (isInitialized.value) return

    const cleanup = listenForTokenUpdates(onTokenUpdated, onTokenExpired, onUserLoggedOut)
    isInitialized.value = true

    onUnmounted(() => {
      cleanup()
      isInitialized.value = false
    })

    return cleanup
  }

  return {
    fastCheckAuth,
    acquireRefreshLock,
    releaseRefreshLock,
    waitForRefresh,
    broadcastTokenUpdate,
    broadcastTokenExpiring,
    broadcastTokenExpired,
    broadcastUserLoggedOut,
    initialize,
  }
}

